import { useEffect, useState } from 'react';
import { AlertTriangle, BellOff, Loader2 } from 'lucide-react';
import { getDisplayErrorMessage } from '@/lib/api';
import { fetchSnoozeLink, pressSnoozeLink, type SnoozeLinkView } from '@/lib/alerts';
import { AuthLayout } from '@/components/AuthLayout';
import formStyles from '@/components/AuthForm.module.css';
import styles from './MagicLinkVerifyPage.module.css';

interface Props {
  onNavigate: (path: string) => void;
}

/**
 * The snooze link in every alert email (Warren, 29 Oct 2026, item 4; ruling F): /alert-snooze?token=..., no sign-in
 * (hoisted above the auth guard like the Done page). Opening the page only reads: Microsoft 365 Safe Links and other
 * scanners open every link in an email, so a GET never snoozes. The one button does, by POST.
 */
export function AlertSnoozePage({ onNavigate }: Props) {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [view, setView] = useState<SnoozeLinkView | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError('This link is incomplete. Open it from the email again.'); return; }
    fetchSnoozeLink(token)
      .then(setView)
      .catch(async (err: unknown) => setError(await getDisplayErrorMessage(err, 'This link is no longer valid.')));
  }, [token]);

  const press = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try { setView(await pressSnoozeLink(token)); } catch (err) { setError(await getDisplayErrorMessage(err, 'Could not snooze it. Try again.')); } finally { setBusy(false); }
  };

  const name = view ? (view.charger_label === view.identity ? view.charger_label : `${view.charger_label} (${view.identity})`) : '';
  return (
    <AuthLayout title={view ? view.location_name || 'Charger alerts' : 'Charger alerts'} subtitle="Snooze alert emails">
      {!view && !error && <div className={styles.spinnerWrap}><Loader2 size={36} className={styles.spinner} /></div>}
      {error && (
        <div className={styles.errorStack}>
          <div className={formStyles.serverError} role="alert">
            <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            {error}
          </div>
          {!view && <button type="button" className={formStyles.btnSecondary} onClick={() => onNavigate('/login')}>Go to sign in</button>}
        </div>
      )}
      {view && (
        <div className={styles.errorStack}>
          <p>Charger: <strong>{name}</strong></p>
          {view.state === 'open' && <>
            <p>Snooze the alert emails about this charger for {view.duration_label}? The morning summary still lists it.</p>
            <button type="button" className={formStyles.submitBtn} disabled={busy} onClick={() => { void press(); }}>
              {busy ? 'Snoozing...' : `Snooze ${view.duration_label}`}
            </button>
          </>}
          {view.state === 'withdrawn' && <p role="status">
            EV Initiative turned this charger's alert emails back on after this email was sent, so this link no longer
            snoozes them. The link in a newer alert email still does.</p>}
          {view.state === 'snoozed' && view.snoozed_until && <p role="status">
            <BellOff size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Snoozed until {new Date(view.snoozed_until).toLocaleString()}. Nothing more to do.</p>}
        </div>
      )}
    </AuthLayout>
  );
}

export default AlertSnoozePage;
